import React from 'react';

// --- Helper Components ---
const Icon = ({ path, className = "w-6 h-6" }) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
        <path strokeLinecap="round" strokeLinejoin="round" d={path} />
    </svg>
);

const Card = ({ title, children, className = "" }) => (
    <div className={`bg-black bg-opacity-20 backdrop-blur-2xl rounded-2xl p-6 md:p-8 border border-white border-opacity-10 shadow-lg ${className}`}>
        {title && <h3 className="text-2xl font-bold text-white mb-4">{title}</h3>}
        {children}
    </div>
);

// --- Main Sectional Components ---

const Header = () => (
    <header className="fixed top-0 left-0 right-0 z-50 p-4 bg-transparent print:hidden">
        <div className="container mx-auto flex justify-end items-center">
        </div>
    </header>
);

const Hero = () => (
    <section className="min-h-screen flex items-center justify-center text-center text-white pt-20">
        <div className="container mx-auto px-4">
            <div className="inline-flex items-center justify-center space-x-2 bg-indigo-500/20 text-indigo-300 py-1.5 px-4 rounded-full text-sm font-semibold mb-6 border border-indigo-500/30">
                <span className="relative flex h-2 w-2 mr-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500"></span>
                </span>
                J&C Enterprise Content OS
            </div>
            <h1 className="text-5xl md:text-7xl font-black tracking-tight mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-white">
                4 Languages. Infinite Scale. <br /> Zero Recording.
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-4xl mx-auto mb-10 leading-relaxed">
                We are eliminating the weakest link in content creation: <strong className="text-white">human creators and scriptwriters.</strong> <br className="hidden md:block"/>
                Deploy a fully automated pipeline capable of scaling from 30 to 120+ videos per page, with zero increase in production cost.
            </p>
        </div>
    </section>
);

const TheBottleneck = () => (
    <section className="py-20 text-white">
        <div className="container mx-auto px-4 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-12">Eliminating The Weakest Link</h2>
            <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
                <div className="bg-red-900/10 border border-red-500/20 rounded-3xl p-8 opacity-80">
                    <div className="w-16 h-16 rounded-full bg-red-500/20 mx-auto mb-4 flex items-center justify-center text-2xl">😫</div>
                    <h3 className="text-2xl font-bold mb-4">The Old Model</h3>
                    <p className="text-white/70 mb-4">Hiring native creators and scriptwriters for French, German, Spanish, etc. Dealing with shoot delays, reshoots, massive payroll overhead, and rigid output limits.</p>
                    <p className="text-sm font-bold text-red-400">Bottleneck: Human Recording & Writing Time</p>
                </div>
                <div className="bg-green-900/10 border border-green-500/30 rounded-3xl p-8 shadow-[0_0_50px_rgba(74,222,128,0.05)]">
                    <div className="w-16 h-16 rounded-full bg-green-500/20 mx-auto mb-4 flex items-center justify-center text-2xl">⚡</div>
                    <h3 className="text-2xl font-bold mb-4">The OS Model</h3>
                    <p className="text-white/70 mb-4">4 digital avatars. Perfect pronunciation. Instant generation. Scale from 30 videos per month to 60, 90, or 120+ <strong className="text-white">for the exact same cost.</strong></p>
                    <p className="text-sm font-bold text-green-400">Bottleneck: Removed</p>
                </div>
            </div>
        </div>
    </section>
);

const Workflow = () => (
    <section className="py-24 text-white bg-black/20">
        <div className="container mx-auto px-4">
            <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-bold mb-4">The 5-Step Automated Workflow</h2>
                <p className="text-xl text-white/70 max-w-2xl mx-auto">How your multi-lingual videos get produced with near-zero friction.</p>
            </div>
            
            <div className="max-w-5xl mx-auto space-y-6">
                {[
                    { step: "1", title: "Ideation & Scripting", desc: "AI algorithms scrape top-performing language content and auto-generate 80% of the script with high-retention hooks." },
                    { step: "2", title: "Project Manager Approval", desc: "Your PM logs into the dashboard, reviews the AI scripts, makes minor tweaks, and clicks 'Approve'." },
                    { step: "3", title: "AI Generation", desc: "The approved script is automatically pushed to the correct Avatar (HeyGen) and Voice (11Labs). The raw video is rendered instantly." },
                    { step: "4", title: "Editor Polish", desc: "The raw avatar footage is pushed to your editor to add B-roll, visual effects, and dynamic captions. High quality is maintained." },
                    { step: "5", title: "Final Review & Publish", desc: "A final check is done, AI generates the SEO-optimized captions, and Blotato automatically schedules/posts to all platforms." }
                ].map((item, i) => (
                    <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-6 flex items-center md:items-start gap-6 hover:bg-white/10 transition-colors">
                        <div className="flex-shrink-0 w-12 h-12 bg-indigo-500 rounded-full flex items-center justify-center font-black text-xl shadow-lg shadow-indigo-500/30">
                            {item.step}
                        </div>
                        <div>
                            <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                            <p className="text-white/70">{item.desc}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    </section>
);

const TheCostShock = () => (
    <section className="py-24 text-white bg-black/30">
        <div className="container mx-auto px-4">
            <h2 className="text-4xl md:text-5xl font-bold text-center mb-16">The True Value of Scale</h2>
            
            <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12">
                {/* Traditional Cost */}
                <div className="bg-red-900/10 border border-red-500/20 rounded-3xl p-8 relative overflow-hidden">
                    <div className="absolute top-0 right-0 bg-red-500/20 text-red-300 px-4 py-1 rounded-bl-xl text-xs font-bold tracking-widest uppercase">The Old Way</div>
                    <h3 className="text-2xl font-bold mb-2">Manual 4-Language Setup</h3>
                    <p className="text-white/60 mb-8">Hiring human creators and scriptwriters for 4 distinct pages.</p>
                    
                    <div className="space-y-6">
                        <div className="flex justify-between items-center border-b border-white/10 pb-4">
                            <span className="text-white/80">Creators & Scriptwriters (4 Languages)</span>
                            <span className="font-mono text-xl">~₹2,00,000 / mo</span>
                        </div>
                        <div className="flex justify-between items-center border-b border-white/10 pb-4">
                            <span className="text-white/80">Cost to Scale (30 ➝ 60 Videos/mo)</span>
                            <span className="font-mono text-xl text-red-400">Doubles (~₹4L)</span>
                        </div>
                        <div className="flex justify-between items-center pt-4">
                            <span className="text-white font-bold text-lg">Year 1 Base Spend</span>
                            <span className="font-mono text-3xl font-bold text-red-400">~₹24,00,000</span>
                        </div>
                    </div>
                </div>
                
                {/* OS Cost */}
                <div className="bg-green-900/10 border border-green-500/30 rounded-3xl p-8 relative overflow-hidden shadow-[0_0_50px_rgba(74,222,128,0.05)]">
                    <div className="absolute top-0 right-0 bg-green-500/20 text-green-300 px-4 py-1 rounded-bl-xl text-xs font-bold tracking-widest uppercase">The OS Advantage</div>
                    <h3 className="text-2xl font-bold mb-2">Enterprise Content OS</h3>
                    <p className="text-white/60 mb-8">Infrastructure you own, running at pure API cost. Editors remain.</p>
                    
                    <div className="space-y-6">
                        <div className="flex justify-between items-center border-b border-white/10 pb-2">
                            <span className="text-white/80">4 Avatars (HeyGen + 11Labs)</span>
                            <span className="font-mono text-lg text-green-300">~₹24,000 / mo</span>
                        </div>
                        <div className="flex justify-between items-center border-b border-white/10 pb-2">
                            <span className="text-white/80">Omni-Posting (Blotato)</span>
                            <span className="font-mono text-lg text-green-300">~₹2,500 / mo</span>
                        </div>
                        <div className="flex justify-between items-center border-b border-white/10 pb-2">
                            <span className="text-white/80">AI APIs (Scripts/Research)</span>
                            <span className="font-mono text-lg text-green-300">~₹6,000 / mo</span>
                        </div>
                        <div className="flex justify-between items-center border-b border-white/10 pb-4 bg-green-500/10 p-2 rounded">
                            <span className="text-white font-bold">Cost to Scale (30 ➝ 120 Videos/mo)</span>
                            <span className="font-mono font-bold text-green-400">Zero Increase</span>
                        </div>
                        <div className="flex justify-between items-center pt-2">
                            <span className="text-white font-bold text-lg">Year 1 Base Spend</span>
                            <span className="font-mono text-3xl font-bold text-green-400">~₹3,90,000</span>
                        </div>
                    </div>
                </div>
            </div>
            
            <div className="max-w-4xl mx-auto mt-16 text-center">
                <div className="inline-block bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-[2px]">
                    <div className="bg-black rounded-xl p-8">
                        <h4 className="text-2xl font-bold text-white mb-2">Year 1 Base Savings</h4>
                        <p className="text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-green-300 to-emerald-500">~₹20,10,000</p>
                        <p className="text-white/60 mt-4">And the gap only widens as you push more volume through the system.</p>
                    </div>
                </div>
            </div>
        </div>
    </section>
);

const TheInvestment = () => (
    <section className="py-24 text-white">
        <div className="container mx-auto px-4">
            <h2 className="text-4xl md:text-5xl font-bold text-center mb-16">The Setup Investment</h2>
            <div className="max-w-5xl mx-auto grid md:grid-cols-5 gap-8 items-stretch">
                
                <div className="md:col-span-3 bg-black/20 border border-white/10 rounded-3xl p-8 md:p-12 shadow-2xl backdrop-blur-sm relative">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 rounded-full blur-3xl"></div>
                    
                    <div className="mb-8">
                        <span className="text-indigo-400 font-bold tracking-widest text-sm uppercase">One-Time OS Architecture Build</span>
                        <h3 className="text-5xl font-black mt-2">₹3,50,000</h3>
                        <p className="text-white/60 mt-2">A singular fixed investment to build and own your 4-page automated pipeline.</p>
                    </div>
                    
                    <div className="space-y-4">
                        <h4 className="font-bold text-lg border-b border-white/10 pb-2">What we build for you:</h4>
                        <ul className="space-y-3">
                            <li className="flex items-start">
                                <Icon path="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" className="w-6 h-6 text-indigo-400 mr-3 flex-shrink-0" />
                                <span className="text-white/80"><strong>4-Avatar Engineering:</strong> Studio-grade setup of HeyGen and ElevenLabs voice clones for 4 distinct languages.</span>
                            </li>
                            <li className="flex items-start">
                                <Icon path="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" className="w-6 h-6 text-indigo-400 mr-3 flex-shrink-0" />
                                <span className="text-white/80"><strong>Automated Workflows:</strong> Make/Zapier/Apify integrations bridging Competitor Research, AI Scripting, and Rendering.</span>
                            </li>
                            <li className="flex items-start">
                                <Icon path="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" className="w-6 h-6 text-indigo-400 mr-3 flex-shrink-0" />
                                <span className="text-white/80"><strong>Custom Notion Dashboard:</strong> The central mission control hub for your entire cross-language empire.</span>
                            </li>
                        </ul>
                    </div>
                </div>
                
                <div className="md:col-span-2 space-y-6">
                    <Card title="ROI Timeline" className="bg-indigo-900/20 border-indigo-500/20">
                        <p className="text-white/80 text-sm leading-relaxed mb-4">
                            By eliminating creator and scriptwriter retainers, your monthly costs drop significantly while your production cap disappears.
                        </p>
                        <div className="bg-black/40 rounded-xl p-4 text-center border border-indigo-500/30">
                            <p className="text-sm text-indigo-300">Break-Even Point</p>
                            <p className="text-2xl font-bold text-white">~2.1 Months</p>
                        </div>
                        <p className="text-xs text-white/50 text-center mt-3">After Month 2, the system runs entirely on profit margin, scaling at zero extra cost.</p>
                    </Card>
                </div>
                
            </div>
        </div>
    </section>
);

// --- Main App Component ---
export default function JandCOsProposal() {
    return (
        <div className="font-sans leading-relaxed text-white">
            <div className="relative z-10">
                <Header />
                <main>
                    <Hero />
                    <TheBottleneck />
                    <Workflow />
                    <TheCostShock />
                    <TheInvestment />
                    <section className="py-24 text-center pb-32">
                        <h2 className="text-3xl font-bold mb-6">Ready to scale your content empire?</h2>
                        <a href="mailto:socialseowork@gmail.com" className="inline-block bg-white text-black font-bold py-4 px-8 rounded-full hover:bg-indigo-100 transition-colors">
                            Initiate OS Build
                        </a>
                    </section>
                </main>
            </div>
        </div>
    );
}
